import React, { forwardRef, useContext, useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { Element, scroller } from 'react-scroll';

import { getTitleId } from 'components/Pages/Admin/Page/Landing/utils';
import { IBlocksProps } from 'components/shared/ArrayFields/types';
import { getClassNameByLandingPageType } from 'components/shared/LandingPage/utils';
import { LANDING_PAGE_ROLES } from 'constants/index';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useTitleChangeModal } from 'hooks/useTitleChangeModal';
import { waitForElm } from 'utils/common';
import { LANDING_THEMES, BOATS_THEME_COVER_LEFT_ID } from 'utils/itineraries/functions';
import { isDefined } from 'utils/ramda';

import EditableText from '../../EditableText';
import { getPageHeaderHeight } from '../blocks/BlockWrapper/function';
import { LANDING_PAGES_CONTAINER_ID } from '../components/Grid';
import PoweredBy from '../components/PoweredBy';
import LandingPageContext from '../contexts/LandingPageContext';
import LandingPageLayoutContext from '../contexts/LandingPageLayoutContext';
import LandingPageTypeContext, { defaultParams } from '../contexts/LandingPageTypeContext';
import styles from './index.module.css';
import { LandingBlock } from './LandingBlock';
import StartArrow from './StartArrow';
import { IRenderStartArrowProps, IRightSideProps, ISection } from './types';

const renderStartArrowDefault = (props: IRenderStartArrowProps) => {
  const { screens = [], containerId = '', startArrow, isSamplePreview, to } = props;
  if (!(startArrow && !isSamplePreview)) return;
  return <StartArrow to={to || screens[1]} containerId={containerId} />;
};

const RightSide = forwardRef<HTMLDivElement, IRightSideProps>(
  ({ initialAnchorSection, renderStartArrow = renderStartArrowDefault }, ref) => {
    const {
      landingData,
      page,
      readOnly,
      hasPoweredBy,
      onChangePageTitle,
      onAddBlock,
      onUpdateBlock,
      onDeleteBlock,
      onAddBlockInsideSpoiler,
      onUpdateBlockInsideSpoiler,
      onDeleteBlockInsideSpoiler,
      screens,
      pageIndex,
      isItinerariesItem = false,
      isPortal = false,
      isPortalItinerary = false,
      links,
      isCoverPage = false,
      isSamplePreview,
      landingPageType,
      landingTheme,
      virtualized,
    } = useContext(LandingPageContext);
    const {
      disableFirstSectionNameRemoval,
      noPageHeadline,
      centerContent,
      startArrow,
      startArrowContainerId,
      blockAddingAvailable = defaultParams.blockAddingAvailable,
    } = useContext(LandingPageTypeContext);
    const showNavigationModal = useTitleChangeModal();

    const [headlineTop, setHeadlineTop] = useState(0);
    const [mobileNavHeight, setMobileNavHeight] = useState(0);
    const { isTabletOrMobile } = useMediaBreakpoints();
    const pageHeadlineRef = useRef<HTMLDivElement | null>(null);
    const landingBlocks: IBlocksProps[] = 'blocks' in landingData ? (landingData.blocks as IBlocksProps[]) : [];
    const anchoredSections = getAnchoredSectionsFromBlocks(landingBlocks);
    const isMobileNavHidden = isTabletOrMobile && links?.length < 2;
    let blockIndex = -1;
    const visibleBlockIndex = React.useRef(-1);
    const firstBlockerRendered = React.useRef(false);
    const titleIsCenter = 'titlePosition' in landingData && landingData?.titlePosition === 'center';
    const titleMountNodeId = 'titleMountNodeId' in landingData ? landingData?.titleMountNodeId : null;
    const blockerRef = useRef<any>();
    const hasHeadline = !isCoverPage && !noPageHeadline;
    const isDefaultMainLayout = landingTheme !== LANDING_THEMES.default && landingTheme !== LANDING_THEMES.boats;
    const isBoastCoverPage =
      landingTheme === LANDING_THEMES.boats && landingData?.role === LANDING_PAGE_ROLES.COVER_PAGE;
    const hasPreparedFor = isBoastCoverPage && 'preparedFor' in page && page.preparedFor;
    const hasTravelDates = isBoastCoverPage && 'travelDates' in page && page.travelDates;

    useEffect(() => {
      if (!initialAnchorSection) {
        return;
      }
      setTimeout(() => {
        const offset = isTabletOrMobile ? 330 : -120;
        scroller.scrollTo(initialAnchorSection, { containerId: LANDING_PAGES_CONTAINER_ID, offset });
      }, 150);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getRightContent = (enteredValue: number) => {
      const defaultMargin = 34;
      if (enteredValue === 0) return defaultMargin;
      return enteredValue;
    };

    const title = (): string => {
      if ('title' in landingData && isDefined(landingData['title'])) {
        return landingData['title'];
      }

      if ('name' in page && isDefined(page['name'])) {
        return page['name'] as string;
      }

      if ('headline' in landingData && isDefined(landingData['headline'])) {
        return landingData['headline'];
      }

      return '';
    };

    const getMarginTop = () => {
      if (landingTheme === LANDING_THEMES.nomad) {
        return !isTabletOrMobile ? 0 : getRightContent(headlineTop);
      }

      if (landingTheme === LANDING_THEMES.jbd) {
        return !isTabletOrMobile ? 0 : getRightContent(headlineTop);
      }

      return getRightContent(headlineTop);
    };

    const { headerRef } = useContext(LandingPageLayoutContext);
    useEffect(() => {
      waitForElm('#landingHeader').then((pageHeaderElem) => {
        setHeadlineTop(
          getPageHeaderHeight({
            isTabletOrMobile,
            landingTheme,
            headerRef,
            pageHeaderElem,
            isPortal,
          }),
        );
      });
      waitForElm('#mobileNav').then((mobileNavElem) => {
        const height =
          mobileNavElem && mobileNavElem instanceof HTMLElement ? mobileNavElem.getBoundingClientRect().height : 0;
        setMobileNavHeight(height);
      });
    }, [isTabletOrMobile, landingTheme, headerRef]);

    if (virtualized && !virtualized.rendered) {
      return (
        <div
          className={cx(styles.right, 'custom-right', getClassNameByLandingPageType(landingPageType, 'right'))}
          style={{ height: virtualized.height }}
          data-index={pageIndex}
          ref={ref}
        />
      );
    }

    return (
      <div
        className={cx(styles.right, 'custom-right', getClassNameByLandingPageType(landingPageType, 'right'))}
        data-index={pageIndex}
        ref={ref}
      >
        <div
          className={cx(
            styles.rightContent,
            centerContent && styles.rightContentCenter,
            // isItinerariesItem && styles.rightContentItinerary, ---> unused style
            'custom-right-content',
          )}
          style={{
            marginTop: getMarginTop(),
          }}
          id="container"
        >
          {hasHeadline && (
            <div
              className={cx(
                styles.pageHeadline,
                titleIsCenter && styles.pageHeadlineCenter,
                isPortal && styles.pageHeadlinePortal,
                isMobileNavHidden && styles.pageHeadlineNoMenu,
                isItinerariesItem && styles.pageHeadlineItinerary,
                'custom-right-headline',
              )}
              ref={pageHeadlineRef}
              style={{ top: headlineTop + (isDefaultMainLayout ? mobileNavHeight : 0) }}
              id={!isItinerariesItem && !isPortalItinerary ? 'pageHeadline' : undefined}
            >
              <div className={cx(styles.inner, 'custom-right-headline-inner')}>
                <div className={styles.headlineEditorContainer}>
                  {(title() || !readOnly) && (
                    <EditableText
                      text={title()}
                      placeholder="Page title"
                      stripPastedStyles
                      onChange={onChangePageTitle}
                      isReadonly={readOnly}
                      textAlignment={titleIsCenter ? 'center' : 'left'}
                    />
                  )}
                </div>

                {titleMountNodeId && <div id={titleMountNodeId} />}
              </div>
            </div>
          )}
          {renderStartArrow({
            screens,
            containerId: startArrowContainerId,
            startArrow,
            isSamplePreview,
            to: landingTheme === LANDING_THEMES.boats ? BOATS_THEME_COVER_LEFT_ID : undefined,
          })}
          {anchoredSections.map((section, index) => {
            const blockId = getTitleId(section.name, index) || '';

            return (
              <Element name={blockId} key={index} id={blockId} style={{ position: 'relative' }}>
                {section.blocks.map((block, realIndex) => {
                  const isLastBlockInSection = section.blocks.length - 1 === realIndex;
                  const isNextBlocked = index === 0 && (realIndex > 2 || isLastBlockInSection);
                  blockIndex++;

                  const landingBlockProps = {
                    block,
                    blockId,
                    blockIndex,
                    visibleBlockIndex,
                    isNextBlocked,
                    firstBlockerRendered,
                    pageHeadlineRef,
                    blockerRef,

                    blockActions: {
                      onDeleteBlock,
                      onUpdateBlock,
                      onAddBlock,
                      onAddBlockInsideSpoiler,
                      onUpdateBlockInsideSpoiler,
                      onDeleteBlockInsideSpoiler,
                    },

                    typeContextProps: { blockAddingAvailable, disableFirstSectionNameRemoval },
                  };

                  const key = block.id ?? block.originalId ?? blockIndex;

                  return <LandingBlock key={key} {...landingBlockProps} />;
                })}
              </Element>
            );
          })}
          {hasPoweredBy && <PoweredBy />}
          {isBoastCoverPage && (
            <span className="custom-right-prepared-for" onClick={() => showNavigationModal(readOnly)}>
              {hasPreparedFor && <span className="custom-left-subheadline">Prepared for {page.preparedFor}</span>}
              {hasTravelDates && <span className="custom-left-traveldates">{page.travelDates}</span>}
            </span>
          )}
        </div>

        <div className={cx(styles.wavePatternContainer, 'custom-left-wave-pattern')}></div>
      </div>
    );
  },
);

export default RightSide;

function getAnchoredSectionsFromBlocks(blocks: IBlocksProps[] = []) {
  let section: ISection = { name: 'Start of the page', blocks: [] };
  let prevBlockType: string;
  return blocks.reduce((sections: ISection[], block, i: number) => {
    if (block.type === 'header' && !block._destroy) {
      if (!prevBlockType) {
        section.name = block.data.subheadline;
        section.blocks.push(block);
      } else {
        sections.push(section);
        section = { name: block.data.subheadline, blocks: [block] };
      }
    } else {
      section.blocks.push(block);
    }
    prevBlockType = block.type;

    if (i === blocks.length - 1) {
      sections.push(section);
    }

    return sections;
  }, []);
}
